import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, formatSEK, formatNumber } from '../../lib/format-utils';
import { getSalaryReport, getUsers, downloadPayslip } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { FileSpreadsheet, Download, RefreshCw } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '../../components/ui/dialog';
import FortnoxSyncDialog from '../../components/admin/FortnoxSyncDialog';
import MonthNavigator from '../../components/month-navigator';

export default function ReportsPage() {
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [reports, setReports] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState('all');
    const [syncDialogOpen, setSyncDialogOpen] = useState(false);
    const [monthlyNorm, setMonthlyNorm] = useState(0);
    const [deviationsDialog, setDeviationsDialog] = useState({ open: false, employee: '', details: [] });
    const [payslipUser, setPayslipUser] = useState('');
    const [payslipMonth, setPayslipMonth] = useState(new Date().getMonth() + 1);
    const [payslipYear, setPayslipYear] = useState(new Date().getFullYear());
    const [payslipDownloading, setPayslipDownloading] = useState(false);

    useEffect(() => {
        fetchData();
    }, [month, year, selectedUser]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [reportRes, usersRes] = await Promise.all([
                getSalaryReport(month, year, selectedUser === 'all' ? null : selectedUser),
                getUsers()
            ]);
            setReports(reportRes.data.reports || []);
            setMonthlyNorm(reportRes.data.monthly_norm_hours || 0);
            setUsers(usersRes.data);
        } catch (error) {
            toast.error(t('reports.failed_load'));
        } finally {
            setLoading(false);
        }
    };

    const handlePayslipDownload = async () => {
        if (!payslipUser) {
            toast.error(t('reports.payslip_select_employee'));
            return;
        }
        setPayslipDownloading(true);
        try {
            const response = await downloadPayslip(payslipUser, payslipYear, payslipMonth);
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `payslip_${payslipYear}_${String(payslipMonth).padStart(2, '0')}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            toast.success(t('reports.export_success'));
        } catch (error) {
            toast.error(t('reports.failed_export'));
        } finally {
            setPayslipDownloading(false);
        }
    };

    const navigateMonth = (direction) => {
        if (direction === 'prev') {
            if (month === 1) {
                setMonth(12);
                setYear(year - 1);
            } else {
                setMonth(month - 1);
            }
        } else {
            if (month === 12) {
                setMonth(1);
                setYear(year + 1);
            } else {
                setMonth(month + 1);
            }
        }
    };

    return (
        <div className="space-y-6" data-testid="reports-page">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight">{t('reports.title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('reports.subtitle')}</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        className="uppercase tracking-wider"
                        onClick={() => setSyncDialogOpen(true)}
                        data-testid="fortnox-sync-btn"
                    >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        {t('reports.fortnox_sync_button')}
                    </Button>
                </div>
            </div>

            <FortnoxSyncDialog
                open={syncDialogOpen}
                onOpenChange={setSyncDialogOpen}
                defaultMonth={month}
                defaultYear={year}
            />

            <Dialog
                open={deviationsDialog.open}
                onOpenChange={(open) => setDeviationsDialog(d => ({ ...d, open }))}
            >
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{deviationsDialog.employee}</DialogTitle>
                        <DialogDescription>{t('reports.col_deviations')}</DialogDescription>
                    </DialogHeader>
                    <div className="overflow-auto max-h-[60vh]">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b">
                                    <th className="text-left py-2 pr-4 font-semibold">{t('reports.col_date')}</th>
                                    <th className="text-left py-2 pr-4 font-semibold">{t('reports.col_type')}</th>
                                    <th className="text-right py-2 font-semibold">{t('reports.col_hours')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deviationsDialog.details
                                    .slice()
                                    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
                                    .map((d, idx) => (
                                        <tr key={idx} className="border-b last:border-0 hover:bg-muted/50">
                                            <td className="py-2 pr-4 font-mono text-sm">{formatDate(d.date)}</td>
                                            <td className="py-2 pr-4">{d.type}</td>
                                            <td className="py-2 text-right font-mono">{Number(d.hours || 0).toFixed(2)}h</td>
                                        </tr>
                                    ))
                                }
                            </tbody>
                        </table>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Month navigation + employee filter (Task 22.6).
                This page carried TWO hand-rolled navigators — a mobile one and
                a desktop one, each rendering the SAME prev-month-btn /
                next-month-btn ids — which is precisely how the shape drifted
                from the Billing Report's. They are one navigator now.

                The employee filter goes in the navigator's slot BELOW the bar
                rather than beside the label. Inline it was ~192px of control
                sharing the centre with the month, which pushed the month
                visibly off centre — the defect this task exists to remove. The
                filter is unchanged in behaviour and keeps the `user-filter`
                id; the duplicate `user-filter-mobile` control disappears with
                the mobile variant that owned it. */}
            <MonthNavigator
                year={year}
                month={month}
                onPrev={() => navigateMonth('prev')}
                onNext={() => navigateMonth('next')}
            >
                <Select value={selectedUser} onValueChange={setSelectedUser}>
                    <SelectTrigger className="w-full max-w-xs sm:w-48" data-testid="user-filter">
                        <SelectValue placeholder={t('reports.filter_user')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('reports.all_employees')}</SelectItem>
                        {users.map(u => (
                            <SelectItem key={u.id} value={String(u.id)}>{u.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </MonthNavigator>

            {/* Report */}
            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin"></div>
                </div>
            ) : reports.length === 0 ? (
                <Card className="border border-border shadow-none">
                    <CardContent className="p-8 text-center text-muted-foreground">
                        <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 opacity-30" />
                        <p>{t('reports.no_data')}</p>
                    </CardContent>
                </Card>
            ) : (
                <Card className="border border-border shadow-none">
                    <CardContent className="p-0">
                        <div className="w-full overflow-x-auto">
                            <table className="data-table min-w-[1200px]">
                                <thead>
                                    <tr>
                                        <th>{t('reports.col_employee')}</th>
                                        <th>{t('reports.col_monthly_norm')}</th>
                                        <th>{t('reports.col_worked_hours')}</th>
                                        <th>{t('reports.col_flex_earned')}</th>
                                        <th>{t('reports.col_flex_used')}</th>
                                        <th>{t('reports.col_flex_net')}</th>
                                        <th>{t('reports.col_standard_ot')}</th>
                                        <th>{t('reports.col_special_ot')}</th>
                                        <th>{t('reports.col_sick_leave')}</th>
                                        <th>{t('reports.col_vab')}</th>
                                        <th>{t('reports.col_parental_leave')}</th>
                                        <th>{t('reports.col_vacation')}</th>
                                        <th>{t('reports.col_deviations')}</th>
                                        <th>{t('reports.col_miles')}</th>
                                        <th>{t('reports.col_allowance')}</th>
                                        <th>{t('reports.col_receipts')}</th>
                                        <th>{t('reports.col_expenses_pay')}</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {reports.map((report) => {
                                        const isTotal = report.is_total === true || report.user_id === "TOTAL";
                                        const hasDetails =
                                            !isTotal &&
                                            Array.isArray(report.deviations_details) &&
                                            report.deviations_details.length > 0;

                                        return (
                                            <tr key={report.user_id} className={isTotal ? "bg-muted border-t" : "border-b"}>
                                                <td className={`p-3 font-medium ${isTotal ? "font-bold" : ""}`}>
                                                    {report.employee}
                                                </td>

                                                <td className={`p-3 font-mono ${isTotal ? "font-bold" : ""}`}>
                                                    {isTotal
                                                        ? `${monthlyNorm * reports.filter(r => !r.is_total && r.user_id !== 'TOTAL').length}h`
                                                        : `${monthlyNorm}h`}
                                                </td>

                                                <td className={`p-3 font-mono ${isTotal ? "font-bold" : ""}`}>
                                                    {Number(report.total_worked_hours ?? report.worked_hours ?? 0).toFixed(2)}h
                                                </td>

                                                <td className={`p-3 font-mono ${isTotal ? "font-bold" : ""}`}>
                                                    {Number(report.flex_earned || 0).toFixed(2)}h
                                                </td>

                                                <td className={`p-3 font-mono ${isTotal ? "font-bold" : ""}`}>
                                                    {Number(report.flex_used || 0).toFixed(2)}h
                                                </td>

                                                {(() => {
                                                    const net = Number(report.flexhours || 0);
                                                    const colorClass = isTotal
                                                        ? ''
                                                        : net > 0
                                                            ? 'text-green-700 dark:text-green-400'
                                                            : net < 0
                                                                ? 'text-amber-700 dark:text-amber-400'
                                                                : '';
                                                    const sign = net > 0 ? '+' : net < 0 ? '−' : '';
                                                    return (
                                                        <td className={`p-3 font-mono ${isTotal ? 'font-bold' : ''} ${colorClass}`}>
                                                            {sign}{Math.abs(net).toFixed(2)}h
                                                        </td>
                                                    );
                                                })()}

                                                <td className={`p-3 font-mono ${isTotal ? "font-bold" : ""}`}>
                                                    {Number(report.standard_ot || 0).toFixed(2)}h
                                                </td>

                                                <td className={`p-3 font-mono ${isTotal ? "font-bold" : ""}`}>
                                                    {Number(report.special_ot || 0).toFixed(2)}h
                                                </td>

                                                <td className={`p-3 font-mono ${isTotal ? "font-bold" : ""}`}>
                                                    {Number(report.sick_leave || 0).toFixed(2)}h
                                                </td>

                                                <td className={`p-3 font-mono ${isTotal ? "font-bold" : ""}`}>
                                                    {Number(report.vab || 0).toFixed(2)}h
                                                </td>

                                                <td className={`p-3 font-mono ${isTotal ? "font-bold" : ""}`}>
                                                    {Number(report.parental_leave || 0).toFixed(2)}h
                                                </td>

                                                <td className={`p-3 font-mono ${isTotal ? "font-bold" : ""}`}>
                                                    {Number(report.vacation || 0).toFixed(2)}h
                                                </td>

                                                <td className="p-3 text-center">
                                                    {isTotal ? (
                                                        "--"
                                                    ) : hasDetails ? (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-auto px-2 py-1 text-xs text-muted-foreground hover:text-foreground underline font-normal"
                                                            onClick={() => setDeviationsDialog({
                                                                open: true,
                                                                employee: report.employee,
                                                                details: report.deviations_details,
                                                            })}
                                                        >
                                                            {t('reports.show_dates')} ({report.deviations_details.length})
                                                        </Button>
                                                    ) : (
                                                        "--"
                                                    )}
                                                </td>

                                                <td className={`p-3 font-mono ${isTotal ? "font-bold" : ""}`}>
                                                    {formatNumber(Number(report.miles || 0), 1)}
                                                </td>

                                                <td className={`p-3 font-mono ${isTotal ? "font-bold" : ""}`}>
                                                    {formatSEK(Number(report.allowance || 0))}
                                                </td>

                                                <td className={`p-3 font-mono ${isTotal ? "font-bold" : ""}`}>
                                                    {formatSEK(Number(report.receipts || 0))}
                                                </td>

                                                <td className={`p-3 font-mono ${isTotal ? "font-bold" : ""}`}>
                                                    {formatSEK(Number(report.expenses_pay || 0))}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Pay Slip */}
            <Card className="border border-border shadow-none">
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-semibold">{t('reports.payslip_title')}</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4">
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium">{t('reports.col_employee')}</label>
                            <Select value={payslipUser} onValueChange={setPayslipUser} data-testid="payslip-user-select">
                                <SelectTrigger className="w-56">
                                    <SelectValue placeholder={t('reports.payslip_select_employee')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {users.filter(u => u.id !== undefined).map(u => (
                                        <SelectItem key={u.id} value={String(u.id)}>{u.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium">{t('common.month')}</label>
                            <Select value={String(payslipMonth)} onValueChange={v => setPayslipMonth(Number(v))}>
                                <SelectTrigger className="w-36">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                        <SelectItem key={m} value={String(m)}>
                                            {new Date(2000, m - 1, 1).toLocaleString(i18n.language, { month: 'long' })}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium">{t('common.year')}</label>
                            <Select value={String(payslipYear)} onValueChange={v => setPayslipYear(Number(v))}>
                                <SelectTrigger className="w-28">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button
                            className="uppercase tracking-wider sm:self-end"
                            onClick={handlePayslipDownload}
                            disabled={payslipDownloading || !payslipUser}
                            data-testid="payslip-download-btn"
                        >
                            <Download className="w-4 h-4 mr-2" />
                            {payslipDownloading ? t('common.exporting') : t('reports.payslip_download_btn')}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
